<script setup>
import {computed, watch} from "vue";
import {useSnackbarStore} from '@/stores/snackbar';
import {useConfigStore} from "@/stores/config.js";
import {useI18n} from 'vue-i18n';
import { useDisplay, useLocale } from 'vuetify'

const snackbarStore = useSnackbarStore();
const configStore = useConfigStore();

const { locale } = useI18n();
const { current } = useLocale();
const { smAndDown } = useDisplay();

// On mobile every dialog fills the screen, unless it sets `fullscreen` itself.
const defaults = computed(() => ({
  VDialog: {
    fullscreen: smAndDown.value,
  },
}));

watch(() => configStore.currentLang, (newLang) => {
  locale.value = newLang;
  current.value = newLang;
}, { immediate: true });
</script>

<template>
  <v-app id="app">
    <v-defaults-provider :defaults="defaults">
      <v-app-bar
        v-if="configStore.environment && configStore.environment !== 'PRODUCTION'"
        class="top-bar bg-indufastRed"
        density="compact"
        height="40"
        elevation="0"
      >
        <a
          :href="configStore.developerUrl"
          target="_blank"
          class="text-body-2 text-white text-decoration-none text-center w-100 text-uppercase font-weight-bold"
        >
          {{ configStore.environment }} - Indufast
        </a>
      </v-app-bar>
      <router-view />
      <v-snackbar-queue
        v-model="snackbarStore.messages"
        :total-visible="5"
      >
        <template #text="{ item }">
          <span style="white-space: pre">{{ item.text }}</span>
        </template>
        <template #actions="{ props }">
          <v-btn
            icon
            size="small"
            v-bind="props"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar-queue>
    </v-defaults-provider>
  </v-app>
</template>

<style lang="scss">
body {
  font-family: Poppins, sans-serif;
}

#app {
  overflow: auto;
}

.v-overlay-container {
  a,
  a:visited {
    color: rgb(var(--v-theme-secondary));
  }

  a:hover,
  a:focus,
  a:active {
    color: rgb(var(--v-theme-primary));
  }
}

.required label.v-label.v-field-label:after {
  content: " *";
  color: red;
}

th.no-wrap,
td.no-wrap {
  white-space: nowrap;
  width: 0;
}

th.wrap,
td.wrap {
  word-wrap: anywhere;
}

td.center {
  text-align: center;
}

main.v-main {
  background-color: rgb(var(--v-theme-GSDBackground));
}

.stickyLastColumn td:last-child,
.stickyLastColumn th:last-child {
  position: sticky;
  right: 0;
  z-index: 100;
  background-color: rgb(var(--v-theme-surface));
}

.rowHover tr:hover td {
  background-color: #F5F5F5;
}

// Not on mobile (below Vuetify's lg breakpoint): a sticky first column takes up too much of the narrow screen.
@media (min-width: 1145px) {
  .stickyFirstColumn td:first-child,
  .stickyFirstColumn th:first-child {
    position: sticky;
    left: 0;
    z-index: 100;
    background-color: rgb(var(--v-theme-surface));
  }
}

.space-between {
  display: flex !important;
  justify-content: space-between;
}

.markup li {
  margin-left: 20px;
}

.markup p {
  margin: 12px 0;
}

span.read-only {
  opacity: 0.7;
  font-size: 75%;
}
span.read-only::before {
  content: " (";
}
span.read-only::after {
  content: ")";
}

.show-print {
  display: none;
}

.error-messages-only textarea {
  display: none;
}

.white-space-pre-wrap {
  white-space: pre-wrap;
}

@media print {
  .hide-print,
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .v-icon--clickable,
  .v-btn,
  .v-overlay-container,
  a {
    display: none !important;
  }

  .show-print {
    display: inline !important;
  }

  #app {
    overflow: visible !important;
  }

  .no-break {
    break-inside: avoid;
  }

  .v-main {
    --v-layout-left: 0px !important;
    --v-layout-top: 0px !important;
    padding: 0 !important;
    display: block !important;
    height: auto !important;
  }

  .v-container {
    overflow: visible !important;
  }

  .v-card,
  .v-table__wrapper {
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
  }

  .v-card {
    display: block !important;
    box-shadow: none !important;
    border: none !important;
  }

  .v-data-table {
    font-size: 10px !important;

    th, td {
      padding: 2px 4px !important;
      height: auto !important;
      font-size: 10px !important;
    }
  }
}

@media print {
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .v-icon--clickable,
  .v-btn,
  a {
    display: none !important;
  }

  .v-overlay-container {
    display: none !important;
  }

  #app {
    overflow: visible !important;
  }

  .no-break {
    break-inside: avoid;
  }

  .v-main {
    --v-layout-left: 0px !important;
    --v-layout-top: 0px !important;
    padding: 0 !important;
    display: block !important;
    height: auto !important;
  }

  .v-container {
    overflow: visible !important;
  }

  .v-card,
  .v-table__wrapper {
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
  }

  .v-card {
    display: block !important;
    box-shadow: none !important;
    border: none !important;
  }

  .v-data-table {
    font-size: 10px !important;

    th, td {
      padding: 2px 4px !important;
      height: auto !important;
      font-size: 10px !important;
    }
  }

  .body-container {
    padding-top: 35px !important;
  }

  .print-logo {
    display: block !important;
  }

  .text-caption.text-medium-emphasis {
    font-size: 10px !important;
  }
  .text-h6 {
    font-size: 12px !important;
  }

  .budget-chart canvas {
    max-width: 100%;
    height: auto !important;
  }
}
</style>
